import { useCallback, useEffect } from "react";
import { useSolanaWalletStore } from "store/solanaWalletStore";
import { Connection, Transaction, VersionedTransaction } from "@solana/web3.js";
import {
  SOLANA_WALLET_OPTIONS,
  SolanaWalletId,
  SolanaWalletOption,
} from "types/solanaWallet";

type SolanaPublicKey = {
  toBase58?: () => string;
};

type SolanaProvider = {
  connect: (options?: { onlyIfTrusted?: boolean }) => Promise<{
    publicKey?: SolanaPublicKey;
  } | null>;
  disconnect?: () => Promise<void>;
  signTransaction?: (
    transaction: Transaction | VersionedTransaction
  ) => Promise<Transaction | VersionedTransaction>;
  signAndSendTransaction?: (
    transaction: Transaction | VersionedTransaction
  ) => Promise<{ signature?: string } | string>;
  publicKey?: SolanaPublicKey | null;
  isConnected?: boolean;
  on?: (event: string, cb: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, cb: (...args: unknown[]) => void) => void;
};

type SolanaWindow = Window & {
  solana?: SolanaProvider & {
    isPhantom?: boolean;
    isSolflare?: boolean;
    isBackpack?: boolean;
    isTrust?: boolean;
  };
  phantom?: { solana?: SolanaProvider };
  solflare?: SolanaProvider;
  backpack?: SolanaProvider;
  trustwallet?: { solana?: SolanaProvider };
};

type WalletWithInstallState = SolanaWalletOption & {
  isInstalled: boolean;
};

const getAddressFromPublicKey = (publicKey?: SolanaPublicKey | null) => {
  if (!publicKey?.toBase58) return null;

  return publicKey.toBase58();
};

const getProviderByWalletId = (
  walletId: SolanaWalletId
): SolanaProvider | null => {
  if (typeof window === "undefined") return null;

  const solanaWindow = window as SolanaWindow;

  switch (walletId) {
    case "phantom":
      return (
        solanaWindow.phantom?.solana ||
        (solanaWindow.solana?.isPhantom ? solanaWindow.solana : null)
      );
    case "solflare":
      return (
        solanaWindow.solflare ||
        (solanaWindow.solana?.isSolflare ? solanaWindow.solana : null)
      );
    case "backpack":
      return (
        solanaWindow.backpack ||
        (solanaWindow.solana?.isBackpack ? solanaWindow.solana : null)
      );
    case "trustWallet":
      return (
        solanaWindow.trustwallet?.solana ||
        (solanaWindow.solana?.isTrust ? solanaWindow.solana : null)
      );
    default:
      return null;
  }
};

const getInstalledWallets = (): WalletWithInstallState[] =>
  SOLANA_WALLET_OPTIONS.map((wallet) => ({
    ...wallet,
    isInstalled: Boolean(getProviderByWalletId(wallet.id)),
  }));

const getWalletNameById = (walletId: SolanaWalletId | null) =>
  SOLANA_WALLET_OPTIONS.find((wallet) => wallet.id === walletId)?.name ?? null;

export const useSolanaWallet = () => {
  const { address, walletId, setWallet, resetWallet } = useSolanaWalletStore();

  const availableWallets = getInstalledWallets();

  const connectWallet = useCallback(
    async (nextWalletId: SolanaWalletId) => {
      const provider = getProviderByWalletId(nextWalletId);
      if (!provider) {
        throw new Error(
          `${
            getWalletNameById(nextWalletId) || "Selected wallet"
          } is not installed.`
        );
      }

      const connectionResult = await provider.connect();
      const connectedAddress =
        getAddressFromPublicKey(connectionResult?.publicKey) ||
        getAddressFromPublicKey(provider.publicKey);

      if (!connectedAddress) {
        throw new Error("Failed to read wallet address after connect.");
      }

      setWallet({
        walletId: nextWalletId,
        address: connectedAddress,
      });

      return connectedAddress;
    },
    [setWallet]
  );

  const disconnectWallet = useCallback(async () => {
    const provider = walletId ? getProviderByWalletId(walletId) : null;

    try {
      if (provider?.disconnect) {
        await provider.disconnect();
      }
    } finally {
      resetWallet();
    }
  }, [walletId, resetWallet]);

  const sendTransaction = useCallback(
    async ({
      transaction,
      connection,
      latestBlockhash,
    }: {
      transaction: Transaction | VersionedTransaction;
      connection: Connection;
      latestBlockhash?: { blockhash: string; lastValidBlockHeight: number };
    }) => {
      if (!walletId) {
        throw new Error("No Solana wallet connected.");
      }

      const provider = getProviderByWalletId(walletId);
      if (!provider) {
        throw new Error("Connected wallet provider is unavailable.");
      }

      let signature: string | null = null;

      if (provider.signAndSendTransaction) {
        const txResult = await provider.signAndSendTransaction(transaction);
        signature =
          typeof txResult === "string" ? txResult : txResult?.signature || null;
      } else if (provider.signTransaction) {
        const signedTx = await provider.signTransaction(transaction);
        if (!signedTx || typeof signedTx.serialize !== "function") {
          throw new Error("Wallet returned invalid signed transaction.");
        }

        signature = await connection.sendRawTransaction(signedTx.serialize());
      }

      if (!signature) {
        throw new Error("Wallet does not support transaction signing.");
      }

      const confirmationBlockhash =
        latestBlockhash || (await connection.getLatestBlockhash());

      await connection.confirmTransaction(
        {
          signature,
          blockhash: confirmationBlockhash.blockhash,
          lastValidBlockHeight: confirmationBlockhash.lastValidBlockHeight,
        },
        "confirmed"
      );

      return signature;
    },
    [walletId]
  );

  useEffect(() => {
    if (!walletId) return;

    const provider = getProviderByWalletId(walletId);
    if (!provider) {
      resetWallet();
      return;
    }

    const syncAddressFromProvider = () => {
      const nextAddress = getAddressFromPublicKey(provider.publicKey);
      if (!nextAddress) {
        resetWallet();
        return;
      }

      setWallet({
        address: nextAddress,
        walletId,
      });
    };

    const handleAccountChanged = (...args: unknown[]) => {
      const account = args[0] as SolanaPublicKey | null;

      if (!account) {
        resetWallet();
        return;
      }

      const nextAddress = getAddressFromPublicKey(account);

      if (!nextAddress) {
        resetWallet();
        return;
      }

      setWallet({
        address: nextAddress,
        walletId,
      });
    };

    const handleDisconnect = () => {
      resetWallet();
    };

    if (provider.isConnected) {
      syncAddressFromProvider();
    }

    provider.on?.("accountChanged", handleAccountChanged);
    provider.on?.("disconnect", handleDisconnect);

    return () => {
      provider.removeListener?.("accountChanged", handleAccountChanged);
      provider.removeListener?.("disconnect", handleDisconnect);
    };
  }, [walletId, resetWallet, setWallet]);

  return {
    address,
    walletId,
    walletName: getWalletNameById(walletId),
    availableWallets,
    connectWallet,
    disconnectWallet,
    sendTransaction,
  };
};
